import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { usePermissions } from "../../contexts/PermissionsContext";

interface ResourcePermission {
  resource: string;
  actions: string[];
}

interface Role {
  _id: string;
  name: string;
  permissions: ResourcePermission[];
  isSystemRole: boolean;
  isActive: boolean;
  createdBy: {
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface UserAccount {
  userId: string;
  accountId: string;
  role: string;
  roleId?: string | { _id: string; name: string };
  user: User;
}

interface AvailableResource {
  name: string;
  actions: string[];
}

const RoleManagement: React.FC = () => {
  const { user } = useAuth();
  const { refetchPermissions } = usePermissions();
  const [roles, setRoles] = useState<Role[]>([]);
  const [availableResources, setAvailableResources] = useState<
    AvailableResource[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    permissions: [] as ResourcePermission[],
  });
  const [allUsers, setAllUsers] = useState<UserAccount[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Filtered users based on search term
  const filteredUsers = allUsers.filter((ua) => {
    const searchLower = userSearchTerm.toLowerCase();
    const fullName = `${ua.user.firstName} ${ua.user.lastName}`.toLowerCase();
    const email = ua.user.email.toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  useEffect(() => {
    fetchRoles();
    fetchAvailableResources();
    fetchAccountUsers();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".relative")) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserDropdown]);

  // Reset search when modal closes
  useEffect(() => {
    if (!showModal) {
      setUserSearchTerm("");
      setShowUserDropdown(false);
    }
  }, [showModal]);

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/roles`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setRoles(data.data);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError("Failed to fetch roles");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableResources = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${apiUrl}/api/roles/meta/available-resources`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();
      if (data.success) {
        setAvailableResources(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch available resources:", err);
    }
  };

  const fetchAccountUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/users/account-users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setAllUsers(data.data);
        console.log("Fetched account users:", data.data);
      } else {
        console.error("Failed to fetch account users:", data.message);
      }
    } catch (err) {
      console.error("Failed to fetch account users:", err);
    }
  };

  const handleCreate = async () => {
    setEditingRole(null);
    setFormData({
      name: "",
      permissions: availableResources.map((resource) => ({
        resource: resource.name,
        actions: [],
      })),
    });
    setSelectedUserIds([]);
    await fetchAccountUsers(); // Fetch users before showing modal
    setShowModal(true);
  };

  const handleEdit = async (role: Role) => {
    setEditingRole(role);
    // Ensure all resources are represented
    const permissions = availableResources.map((resource) => {
      const existing = role.permissions.find(
        (p) => p.resource === resource.name,
      );
      return {
        resource: resource.name,
        actions: existing ? existing.actions : [],
      };
    });
    setFormData({
      name: role.name,
      permissions,
    });

    // Fetch users first to ensure we have the latest data
    await fetchAccountUsers();

    // Filter users assigned to this role
    // roleId can be a string or an object, so we need to handle both cases
    const usersInRole = allUsers
      .filter((ua) => {
        if (!ua.roleId) return false;
        const roleIdStr = typeof ua.roleId === 'object' ? ua.roleId._id : ua.roleId;
        return roleIdStr === role._id;
      })
      .map((ua) => ua.userId);
    setSelectedUserIds(usersInRole);

    setShowModal(true);
  };

  const handleDelete = async (roleId: string, roleName: string) => {
    if (
      !window.confirm(`Are you sure you want to delete the role "${roleName}"?`)
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/roles/${roleId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        // Refresh permissions for current user in case they were affected
        await refetchPermissions();
        fetchRoles();
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert("Failed to delete role");
      console.error(err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Filter out permissions with no actions selected
    const filteredPermissions = formData.permissions.filter(
      (p) => p.actions.length > 0,
    );

    try {
      const token = localStorage.getItem("token");
      
      let roleId: string;

      // For system roles, skip the role update API call (they can't be modified)
      // Just update user assignments
      if (editingRole?.isSystemRole) {
        roleId = editingRole._id;
        
        // Update user assignments only
        await updateUserAssignments(roleId);

        // Refresh permissions for current user if they were affected
        await refetchPermissions();

        setShowModal(false);
        fetchRoles();
        fetchAccountUsers();
      } else {
        // For custom roles, update the role and then user assignments
        const url = editingRole
          ? `${apiUrl}/api/roles/${editingRole._id}`
          : `${apiUrl}/api/roles`;

        const response = await fetch(url, {
          method: editingRole ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...formData,
            permissions: filteredPermissions,
          }),
        });

        const data = await response.json();
        if (data.success) {
          roleId = editingRole?._id || data.data._id;

          // Update user assignments
          await updateUserAssignments(roleId);

          // Refresh permissions for current user if they were affected
          await refetchPermissions();

          setShowModal(false);
          fetchRoles();
          fetchAccountUsers();
        } else {
          setError(data.message);
        }
      }
    } catch (err) {
      setError("Failed to save role");
      console.error(err);
    }
  };

  const updateUserAssignments = async (roleId: string) => {
    const token = localStorage.getItem("token");

    // Get current users in this role
    // roleId can be a string or an object, so we need to handle both cases
    const currentUsersInRole = allUsers
      .filter((ua) => {
        if (!ua.roleId) return false;
        const roleIdStr = typeof ua.roleId === 'object' ? ua.roleId._id : ua.roleId;
        return roleIdStr === roleId;
      })
      .map((ua) => ua.userId);

    // Users to add (in selectedUserIds but not in currentUsersInRole)
    const usersToAdd = selectedUserIds.filter(
      (uid) => !currentUsersInRole.includes(uid),
    );

    // Users to remove (in currentUsersInRole but not in selectedUserIds)
    const usersToRemove = currentUsersInRole.filter(
      (uid) => !selectedUserIds.includes(uid),
    );

    // Assign role to new users
    for (const userId of usersToAdd) {
      try {
        const response = await fetch(`${apiUrl}/api/roles/${roleId}/assign`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId }),
        });
        const data = await response.json();
        if (!data.success) {
          console.error(
            "Failed to assign user:",
            data.message || "Unknown error",
          );
          setError(data.message || `Failed to assign role to user`);
        }
      } catch (err) {
        console.error("Failed to assign user:", err);
        setError("Failed to assign role. Please try again.");
      }
    }

    // Unassign users (set roleId to null)
    for (const userId of usersToRemove) {
      try {
        const response = await fetch(`${apiUrl}/api/roles/unassign`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId }),
        });
        const data = await response.json();
        if (!data.success) {
          console.error(
            "Failed to unassign user:",
            data.message || "Unknown error",
          );
          setError(data.message || `Failed to unassign role from user`);
        }
      } catch (err) {
        console.error("Failed to unassign user:", err);
        setError("Failed to unassign role. Please try again.");
      }
    }
  };

  const handlePermissionChange = (
    resourceName: string,
    action: string,
    checked: boolean,
  ) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.map((perm) => {
        if (perm.resource === resourceName) {
          return {
            ...perm,
            actions: checked
              ? [...perm.actions, action]
              : perm.actions.filter((a) => a !== action),
          };
        }
        return perm;
      }),
    }));
  };

  // Get the current state of a scoped action (view, add, edit, approve, delete): 'none', 'own', 'all'
  const getScopedActionState = (
    resourceName: string,
    actionBase: "view" | "add" | "edit" | "approve" | "delete",
  ): "none" | "own" | "all" => {
    const perm = formData.permissions.find((p) => p.resource === resourceName);
    if (!perm) return "none";

    const hasAll = perm.actions.includes(`${actionBase}_all`);
    const hasOwn = perm.actions.includes(`${actionBase}_own`);

    if (hasAll) return "all";
    if (hasOwn) return "own";
    return "none";
  };

  // Toggle scoped action through: none -> own -> all -> none
  const toggleScopedAction = (
    resourceName: string,
    actionBase: "view" | "add" | "edit" | "approve" | "delete",
  ) => {
    const currentState = getScopedActionState(resourceName, actionBase);

    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.map((perm) => {
        if (perm.resource === resourceName) {
          let newActions = perm.actions.filter(
            (a) => a !== `${actionBase}_all` && a !== `${actionBase}_own`,
          );

          if (currentState === "none") {
            // none -> own
            newActions.push(`${actionBase}_own`);
          } else if (currentState === "own") {
            // own -> all
            newActions.push(`${actionBase}_all`);
          }
          // all -> none (remove both, which we already did)

          return {
            ...perm,
            actions: newActions,
          };
        }
        return perm;
      }),
    }));
  };

  const isActionChecked = (resourceName: string, action: string): boolean => {
    const perm = formData.permissions.find((p) => p.resource === resourceName);
    return perm ? perm.actions.includes(action) : false;
  };

  // For column-wide toggles of scoped actions
  const toggleAllForScopedAction = (
    actionBase: "view" | "add" | "edit" | "approve" | "delete",
    targetState: "none" | "own" | "all",
  ) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.map((perm) => {
        // Check if this resource supports this action
        const resource = availableResources.find(
          (r) => r.name === perm.resource,
        );
        const hasActionSupport =
          resource &&
          (resource.actions.includes(`${actionBase}_all`) ||
            resource.actions.includes(`${actionBase}_own`));

        // Skip resources that don't support this action
        if (!hasActionSupport) {
          return perm;
        }

        let newActions = perm.actions.filter(
          (a) => a !== `${actionBase}_all` && a !== `${actionBase}_own`,
        );

        if (targetState === "own") {
          newActions.push(`${actionBase}_own`);
        } else if (targetState === "all") {
          newActions.push(`${actionBase}_all`);
        }
        // 'none' means don't add anything

        return {
          ...perm,
          actions: newActions,
        };
      }),
    }));
  };

  // Check if all resources have the same state for a scoped action
  const getAllResourcesStateForAction = (
    actionBase: "view" | "add" | "edit" | "approve" | "delete",
  ): "none" | "own" | "all" | "mixed" => {
    // Only check resources that support this action
    const states = formData.permissions
      .filter((perm) => {
        const resource = availableResources.find(
          (r) => r.name === perm.resource,
        );
        return (
          resource &&
          (resource.actions.includes(`${actionBase}_all`) ||
            resource.actions.includes(`${actionBase}_own`))
        );
      })
      .map((perm) => getScopedActionState(perm.resource, actionBase));

    const uniqueStates = Array.from(new Set(states));

    if (uniqueStates.length === 1) return uniqueStates[0];
    return "mixed";
  };

  const handleSelectAllForResource = (resourceName: string) => {
    const resource = availableResources.find((r) => r.name === resourceName);
    if (!resource) return;

    // Get current state for all available scoped actions in this resource
    const availableActions: ("view" | "add" | "edit" | "approve" | "delete")[] =
      [];

    // Check which actions are available for this resource
    if (
      resource.actions.includes("view_all") ||
      resource.actions.includes("view_own")
    ) {
      availableActions.push("view");
    }
    if (
      resource.actions.includes("add_all") ||
      resource.actions.includes("add_own")
    ) {
      availableActions.push("add");
    }
    if (
      resource.actions.includes("edit_all") ||
      resource.actions.includes("edit_own")
    ) {
      availableActions.push("edit");
    }
    if (
      resource.actions.includes("approve_all") ||
      resource.actions.includes("approve_own")
    ) {
      availableActions.push("approve");
    }
    if (
      resource.actions.includes("delete_all") ||
      resource.actions.includes("delete_own")
    ) {
      availableActions.push("delete");
    }

    // Get current state for available actions only
    const states = availableActions.map((action) =>
      getScopedActionState(resourceName, action),
    );
    const uniqueStates = Array.from(new Set(states));
    const currentState = uniqueStates.length === 1 ? uniqueStates[0] : "none";

    // Cycle: none -> own -> all -> none
    let targetState: "none" | "own" | "all" = "own";
    if (currentState === "own") targetState = "all";
    else if (currentState === "all") targetState = "none";

    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.map((perm) => {
        if (perm.resource === resourceName) {
          let newActions: string[] = [];

          // Handle each available action based on target state
          availableActions.forEach((actionBase) => {
            if (targetState === "own") {
              newActions.push(`${actionBase}_own`);
            } else if (targetState === "all") {
              newActions.push(`${actionBase}_all`);
            }
            // 'none' means don't add anything
          });

          return {
            ...perm,
            actions: newActions,
          };
        }
        return perm;
      }),
    }));
  };

  // Get the current state of the "All" column for a resource
  const getResourceAllState = (
    resourceName: string,
  ): "none" | "own" | "all" | "mixed" => {
    const resource = availableResources.find((r) => r.name === resourceName);
    if (!resource) return "none";

    // Only check states for actions that are available for this resource
    const states: ("none" | "own" | "all")[] = [];

    if (
      resource.actions.includes("view_all") ||
      resource.actions.includes("view_own")
    ) {
      states.push(getScopedActionState(resourceName, "view"));
    }
    if (
      resource.actions.includes("add_all") ||
      resource.actions.includes("add_own")
    ) {
      states.push(getScopedActionState(resourceName, "add"));
    }
    if (
      resource.actions.includes("edit_all") ||
      resource.actions.includes("edit_own")
    ) {
      states.push(getScopedActionState(resourceName, "edit"));
    }
    if (
      resource.actions.includes("approve_all") ||
      resource.actions.includes("approve_own")
    ) {
      states.push(getScopedActionState(resourceName, "approve"));
    }
    if (
      resource.actions.includes("delete_all") ||
      resource.actions.includes("delete_own")
    ) {
      states.push(getScopedActionState(resourceName, "delete"));
    }

    const uniqueStates = Array.from(new Set(states));

    if (uniqueStates.length === 1) return uniqueStates[0];
    return "mixed";
  };

  // Get the overall state of the entire matrix
  const getMatrixAllState = (): "none" | "own" | "all" | "mixed" => {
    // Collect all action states, only for actions that are available for each resource
    const allStates: ("none" | "own" | "all")[] = [];

    availableResources.forEach((resource) => {
      // Check only the available actions for this resource
      if (
        resource.actions.includes("view_all") ||
        resource.actions.includes("view_own")
      ) {
        allStates.push(getScopedActionState(resource.name, "view"));
      }
      if (
        resource.actions.includes("add_all") ||
        resource.actions.includes("add_own")
      ) {
        allStates.push(getScopedActionState(resource.name, "add"));
      }
      if (
        resource.actions.includes("edit_all") ||
        resource.actions.includes("edit_own")
      ) {
        allStates.push(getScopedActionState(resource.name, "edit"));
      }
      if (
        resource.actions.includes("approve_all") ||
        resource.actions.includes("approve_own")
      ) {
        allStates.push(getScopedActionState(resource.name, "approve"));
      }
      if (
        resource.actions.includes("delete_all") ||
        resource.actions.includes("delete_own")
      ) {
        allStates.push(getScopedActionState(resource.name, "delete"));
      }
    });

    const uniqueStates = Array.from(new Set(allStates));

    if (uniqueStates.length === 1) return uniqueStates[0];
    return "mixed";
  };

  // Toggle all permissions in the entire matrix
  const handleToggleAllMatrix = () => {
    const currentState = getMatrixAllState();

    // Cycle: none/mixed -> own -> all -> none
    let targetState: "none" | "own" | "all" = "own";
    if (currentState === "own") targetState = "all";
    else if (currentState === "all") targetState = "none";
    // If mixed or none, go to 'own'

    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.map((perm) => {
        const resource = availableResources.find(
          (r) => r.name === perm.resource,
        );
        if (!resource) return perm;

        let newActions: string[] = [];

        // Handle each action based on target state, only for available actions
        (["view", "add", "edit", "approve", "delete"] as const).forEach(
          (actionBase) => {
            // Check if this action is available for this resource
            const hasAction =
              resource.actions.includes(`${actionBase}_all`) ||
              resource.actions.includes(`${actionBase}_own`);
            if (!hasAction) return;

            if (targetState === "own") {
              newActions.push(`${actionBase}_own`);
            } else if (targetState === "all") {
              newActions.push(`${actionBase}_all`);
            }
            // 'none' means don't add anything
          },
        );

        return {
          ...perm,
          actions: newActions,
        };
      }),
    }));
  };

  const handleSelectAllForAction = (action: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.map((perm) => {
        if (checked) {
          // Add action if not already present
          return {
            ...perm,
            actions: perm.actions.includes(action)
              ? perm.actions
              : [...perm.actions, action],
          };
        } else {
          // Remove action
          return {
            ...perm,
            actions: perm.actions.filter((a) => a !== action),
          };
        }
      }),
    }));
  };

  const handleToggleAllPermissions = (checked: boolean) => {
    if (checked) {
      // Select all permissions for all resources
      setFormData((prev) => ({
        ...prev,
        permissions: prev.permissions.map((perm) => {
          const resource = availableResources.find(
            (r) => r.name === perm.resource,
          );
          return {
            ...perm,
            actions: resource ? [...resource.actions] : perm.actions,
          };
        }),
      }));
    } else {
      // Deselect all permissions
      setFormData((prev) => ({
        ...prev,
        permissions: prev.permissions.map((perm) => ({
          ...perm,
          actions: [],
        })),
      }));
    }
  };

  const isAllPermissionsSelected = (): boolean => {
    return formData.permissions.every((perm) => {
      const resource = availableResources.find((r) => r.name === perm.resource);
      if (!resource) return false;
      return resource.actions.every((action) => perm.actions.includes(action));
    });
  };

  const isAllSelectedForResource = (resourceName: string): boolean => {
    const resource = availableResources.find((r) => r.name === resourceName);
    const perm = formData.permissions.find((p) => p.resource === resourceName);
    if (!resource || !perm) return false;
    return resource.actions.every((action) => perm.actions.includes(action));
  };

  const isAllSelectedForAction = (action: string): boolean => {
    return formData.permissions.every((perm) => perm.actions.includes(action));
  };

  const handleUserToggle = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Loading roles...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            Role Management
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Create and manage custom roles with specific permissions
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors"
        >
          + Create Role
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-error-700 dark:bg-error-900 dark:border-red-700 dark:text-error-300 rounded-lg">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Role Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Permissions
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
            {roles.map((role) => (
              <tr
                key={role._id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {role.name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {role.isSystemRole ? (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300">
                      System
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-success-100 dark:bg-success-800 text-success-700 dark:text-success-300">
                      Custom
                    </span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {role.permissions.length} resource
                    {role.permissions.length !== 1 ? "s" : ""}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleEdit(role)}
                    className="text-accent-600 hover:text-accent-900 dark:text-accent-400 dark:hover:text-accent-300 mr-4"
                  >
                    View
                  </button>
                  {!role.isSystemRole && (
                    <button
                      onClick={() => handleDelete(role._id, role.name)}
                      className="text-error-600 hover:text-error-900 dark:text-error-400 dark:hover:text-error-300"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {roles.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="px-6 py-12 text-center text-gray-500 dark:text-gray-400"
                >
                  No roles found. Create your first custom role to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {editingRole
                    ? editingRole.isSystemRole
                      ? "View Role"
                      : "Edit Role"
                    : "Create New Role"}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Role Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                    required
                    disabled={editingRole?.isSystemRole}
                  />
                </div>

                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Permissions
                    </h4>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    <strong>Own:</strong> Access to own records only |{" "}
                    <strong>All:</strong> Access to all records
                  </div>
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            <button
                              type="button"
                              onClick={handleToggleAllMatrix}
                              disabled={editingRole?.isSystemRole}
                              className={`px-2 py-1 rounded text-xs font-medium min-w-[50px] ${
                                getMatrixAllState() === "all"
                                  ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                                  : getMatrixAllState() === "own"
                                    ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300"
                                    : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400"
                              }`}
                              title="Toggle all permissions in the entire matrix"
                            >
                              {getMatrixAllState() === "mixed"
                                ? "Mix"
                                : getMatrixAllState() === "all"
                                  ? "All"
                                  : getMatrixAllState() === "own"
                                    ? "Own"
                                    : "None"}
                            </button>
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase sticky left-0 bg-gray-50 dark:bg-gray-700 z-10">
                            Resource
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            <div className="flex flex-col items-center gap-1">
                              <span>View</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const state =
                                    getAllResourcesStateForAction("view");
                                  const nextState =
                                    state === "all"
                                      ? "none"
                                      : state === "own"
                                        ? "all"
                                        : "own";
                                  toggleAllForScopedAction("view", nextState);
                                }}
                                disabled={editingRole?.isSystemRole}
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  getAllResourcesStateForAction("view") ===
                                  "all"
                                    ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                                    : getAllResourcesStateForAction("view") ===
                                        "own"
                                      ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300"
                                      : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400"
                                }`}
                                title="Toggle all View permissions"
                              >
                                {getAllResourcesStateForAction("view") ===
                                "mixed"
                                  ? "Mixed"
                                  : getAllResourcesStateForAction("view") ===
                                      "all"
                                    ? "All"
                                    : getAllResourcesStateForAction("view") ===
                                        "own"
                                      ? "Own"
                                      : "None"}
                              </button>
                            </div>
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            <div className="flex flex-col items-center gap-1">
                              <span>Add</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const state =
                                    getAllResourcesStateForAction("add");
                                  const nextState =
                                    state === "all"
                                      ? "none"
                                      : state === "own"
                                        ? "all"
                                        : "own";
                                  toggleAllForScopedAction("add", nextState);
                                }}
                                disabled={editingRole?.isSystemRole}
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  getAllResourcesStateForAction("add") === "all"
                                    ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                                    : getAllResourcesStateForAction("add") ===
                                        "own"
                                      ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300"
                                      : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400"
                                }`}
                                title="Toggle all Add permissions"
                              >
                                {getAllResourcesStateForAction("add") ===
                                "mixed"
                                  ? "Mixed"
                                  : getAllResourcesStateForAction("add") ===
                                      "all"
                                    ? "All"
                                    : getAllResourcesStateForAction("add") ===
                                        "own"
                                      ? "Own"
                                      : "None"}
                              </button>
                            </div>
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            <div className="flex flex-col items-center gap-1">
                              <span>Edit</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const state =
                                    getAllResourcesStateForAction("edit");
                                  const nextState =
                                    state === "all"
                                      ? "none"
                                      : state === "own"
                                        ? "all"
                                        : "own";
                                  toggleAllForScopedAction("edit", nextState);
                                }}
                                disabled={editingRole?.isSystemRole}
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  getAllResourcesStateForAction("edit") ===
                                  "all"
                                    ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                                    : getAllResourcesStateForAction("edit") ===
                                        "own"
                                      ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300"
                                      : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400"
                                }`}
                                title="Toggle all Edit permissions"
                              >
                                {getAllResourcesStateForAction("edit") ===
                                "mixed"
                                  ? "Mixed"
                                  : getAllResourcesStateForAction("edit") ===
                                      "all"
                                    ? "All"
                                    : getAllResourcesStateForAction("edit") ===
                                        "own"
                                      ? "Own"
                                      : "None"}
                              </button>
                            </div>
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            <div className="flex flex-col items-center gap-1">
                              <span>Approve</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const state =
                                    getAllResourcesStateForAction("approve");
                                  const nextState =
                                    state === "all"
                                      ? "none"
                                      : state === "own"
                                        ? "all"
                                        : "own";
                                  toggleAllForScopedAction(
                                    "approve",
                                    nextState,
                                  );
                                }}
                                disabled={editingRole?.isSystemRole}
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  getAllResourcesStateForAction("approve") ===
                                  "all"
                                    ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                                    : getAllResourcesStateForAction(
                                          "approve",
                                        ) === "own"
                                      ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300"
                                      : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400"
                                }`}
                                title="Toggle all Approve permissions"
                              >
                                {getAllResourcesStateForAction("approve") ===
                                "mixed"
                                  ? "Mixed"
                                  : getAllResourcesStateForAction("approve") ===
                                      "all"
                                    ? "All"
                                    : getAllResourcesStateForAction(
                                          "approve",
                                        ) === "own"
                                      ? "Own"
                                      : "None"}
                              </button>
                            </div>
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            <div className="flex flex-col items-center gap-1">
                              <span>Delete</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const state =
                                    getAllResourcesStateForAction("delete");
                                  const nextState =
                                    state === "all"
                                      ? "none"
                                      : state === "own"
                                        ? "all"
                                        : "own";
                                  toggleAllForScopedAction("delete", nextState);
                                }}
                                disabled={editingRole?.isSystemRole}
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  getAllResourcesStateForAction("delete") ===
                                  "all"
                                    ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                                    : getAllResourcesStateForAction(
                                          "delete",
                                        ) === "own"
                                      ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300"
                                      : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400"
                                }`}
                                title="Toggle all Delete permissions"
                              >
                                {getAllResourcesStateForAction("delete") ===
                                "mixed"
                                  ? "Mixed"
                                  : getAllResourcesStateForAction("delete") ===
                                      "all"
                                    ? "All"
                                    : getAllResourcesStateForAction(
                                          "delete",
                                        ) === "own"
                                      ? "Own"
                                      : "None"}
                              </button>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                        {availableResources.map((resource) => {
                          const viewState = getScopedActionState(
                            resource.name,
                            "view",
                          );
                          const addState = getScopedActionState(
                            resource.name,
                            "add",
                          );
                          const editState = getScopedActionState(
                            resource.name,
                            "edit",
                          );
                          const approveState = getScopedActionState(
                            resource.name,
                            "approve",
                          );
                          const deleteState = getScopedActionState(
                            resource.name,
                            "delete",
                          );
                          const resourceAllState = getResourceAllState(
                            resource.name,
                          );

                          return (
                            <tr key={resource.name}>
                              <td className="px-2 py-3 text-center">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleSelectAllForResource(resource.name)
                                  }
                                  disabled={editingRole?.isSystemRole}
                                  className={`px-2 py-1 rounded text-xs font-medium min-w-[50px] ${
                                    resourceAllState === "all"
                                      ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                                      : resourceAllState === "own"
                                        ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300"
                                        : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400"
                                  }`}
                                  title={`Toggle all permissions for ${resource.name} (currently: ${resourceAllState})`}
                                >
                                  {resourceAllState === "mixed"
                                    ? "Mix"
                                    : resourceAllState === "all"
                                      ? "All"
                                      : resourceAllState === "own"
                                        ? "Own"
                                        : "None"}
                                </button>
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 sticky left-0 bg-white dark:bg-gray-800 z-10">
                                {resource.name}
                              </td>

                              {/* View 3-way toggle */}
                              <td className="px-4 py-3 text-center">
                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleScopedAction(resource.name, "view")
                                  }
                                  disabled={editingRole?.isSystemRole}
                                  className={`px-3 py-1.5 rounded text-xs font-medium min-w-[60px] ${
                                    viewState === "all"
                                      ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                                      : viewState === "own"
                                        ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300"
                                        : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400"
                                  }`}
                                >
                                  {viewState === "all"
                                    ? "All"
                                    : viewState === "own"
                                      ? "Own"
                                      : "None"}
                                </button>
                              </td>

                              {/* Add 3-way toggle */}
                              <td className="px-4 py-3 text-center">
                                {!resource.actions.includes("add_all") &&
                                !resource.actions.includes("add_own") ? (
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    N/A
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      toggleScopedAction(resource.name, "add")
                                    }
                                    disabled={editingRole?.isSystemRole}
                                    className={`px-3 py-1.5 rounded text-xs font-medium min-w-[60px] ${
                                      addState === "all"
                                        ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                                        : addState === "own"
                                          ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300"
                                          : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400"
                                    }`}
                                  >
                                    {addState === "all"
                                      ? "All"
                                      : addState === "own"
                                        ? "Own"
                                        : "None"}
                                  </button>
                                )}
                              </td>

                              {/* Edit 3-way toggle */}
                              <td className="px-4 py-3 text-center">
                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleScopedAction(resource.name, "edit")
                                  }
                                  disabled={editingRole?.isSystemRole}
                                  className={`px-3 py-1.5 rounded text-xs font-medium min-w-[60px] ${
                                    editState === "all"
                                      ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                                      : editState === "own"
                                        ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300"
                                        : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400"
                                  }`}
                                >
                                  {editState === "all"
                                    ? "All"
                                    : editState === "own"
                                      ? "Own"
                                      : "None"}
                                </button>
                              </td>

                              {/* Approve 3-way toggle */}
                              <td className="px-4 py-3 text-center">
                                {!resource.actions.includes("approve_all") &&
                                !resource.actions.includes("approve_own") ? (
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    N/A
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      toggleScopedAction(
                                        resource.name,
                                        "approve",
                                      )
                                    }
                                    disabled={editingRole?.isSystemRole}
                                    className={`px-3 py-1.5 rounded text-xs font-medium min-w-[60px] ${
                                      approveState === "all"
                                        ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                                        : approveState === "own"
                                          ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300"
                                          : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400"
                                    }`}
                                  >
                                    {approveState === "all"
                                      ? "All"
                                      : approveState === "own"
                                        ? "Own"
                                        : "None"}
                                  </button>
                                )}
                              </td>

                              {/* Delete 3-way toggle */}
                              <td className="px-4 py-3 text-center">
                                {!resource.actions.includes("delete_all") &&
                                !resource.actions.includes("delete_own") ? (
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    N/A
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      toggleScopedAction(
                                        resource.name,
                                        "delete",
                                      )
                                    }
                                    disabled={editingRole?.isSystemRole}
                                    className={`px-3 py-1.5 rounded text-xs font-medium min-w-[60px] ${
                                      deleteState === "all"
                                        ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300"
                                        : deleteState === "own"
                                          ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300"
                                          : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400"
                                    }`}
                                  >
                                    {deleteState === "all"
                                      ? "All"
                                      : deleteState === "own"
                                        ? "Own"
                                        : "None"}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Assign Users
                  </label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Type to search users..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        onFocus={() => setShowUserDropdown(true)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                      />

                      {showUserDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {allUsers.length === 0 ? (
                            <div className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                              No users available
                            </div>
                          ) : filteredUsers.length === 0 ? (
                            <div className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                              No users found
                            </div>
                          ) : (
                            <div className="py-1">
                              {filteredUsers.map((userAccount) => (
                                <div
                                  key={userAccount.userId}
                                  onClick={() =>
                                    handleUserToggle(userAccount.userId)
                                  }
                                  className={`flex items-center space-x-3 px-4 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
                                    selectedUserIds.includes(userAccount.userId)
                                      ? "bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50"
                                      : ""
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedUserIds.includes(
                                      userAccount.userId,
                                    )}
                                    onChange={() => {}}
                                    className="w-4 h-4 text-primary-600 dark:text-primary-500 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 dark:focus:ring-primary-600 pointer-events-none"
                                  />
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {userAccount.user.firstName}{" "}
                                      {userAccount.user.lastName}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      {userAccount.user.email}
                                    </div>
                                  </div>
                                  {userAccount.roleId &&
                                    (() => {
                                      const roleIdStr = typeof userAccount.roleId === 'object' ? userAccount.roleId._id : userAccount.roleId;
                                      return roleIdStr !== editingRole?._id;
                                    })() && (
                                      <span className="text-xs px-2 py-1 bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-300 rounded">
                                        Has other role
                                      </span>
                                    )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Selected users badges */}
                    {selectedUserIds.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {selectedUserIds.map((userId) => {
                          const userAccount = allUsers.find(
                            (u) => u.userId === userId,
                          );
                          if (!userAccount) return null;
                          return (
                            <span
                              key={userId}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-300 rounded-full text-sm"
                            >
                              {userAccount.user.firstName}{" "}
                              {userAccount.user.lastName}
                              <button
                                type="button"
                                onClick={() => handleUserToggle(userId)}
                                className="hover:bg-primary-200 dark:hover:bg-primary-800 rounded-full p-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {selectedUserIds.length} user(s) selected. Users with
                      existing roles will be reassigned to this role.
                    </p>
                  </div>

                {!editingRole?.isSystemRole && (
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {editingRole ? "Update Role" : "Create Role"}
                    </button>
                  </div>
                )}
                {editingRole?.isSystemRole && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Close
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
